import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Globe, Brain, UtensilsCrossed, Wrench } from 'lucide-react';
import useMobile from '../hooks/useMobile';

const Services = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);
  const containerRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const servicesInView = useInView(servicesRef, { once: true, margin: "-100px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const isMobile = useMobile();
  const bgY = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["0%", "20%"]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const services = [
    {
      icon: Globe,
      title: "Website Design & Development",
      description: "Modern, scalable, conversion-focused websites that establish your digital presence with authority. From sleek landing pages to comprehensive web applications.",
      features: ["Responsive Design", "SEO Optimized", "Fast Performance", "Custom CMS"],
      gradient: "from-blue-haze/20 to-transparent",
      image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: Brain,
      title: "AI-Powered Business Websites",
      description: "Smart workflows, automation, and intelligent systems that transform how your business operates. Leverage the power of AI to stay ahead of the competition.",
      features: ["AI Integration", "Smart Automation", "Data Analytics", "Chatbots"],
      gradient: "from-violet-glow/20 to-transparent",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: UtensilsCrossed,
      title: "Restaurant & Business Systems",
      description: "Complete digital infrastructure for restaurants and businesses. QR menus, admin dashboards, ordering systems, and scalable solutions built for growth.",
      features: ["QR Menu Systems", "Order Management", "Admin Dashboard", "Inventory Tracking"],
      gradient: "from-gold/15 to-transparent",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80"
    },
    {
      icon: Wrench,
      title: "Custom Digital Solutions",
      description: "Tailored systems built for your specific business needs. When off-the-shelf solutions don't cut it, we build exactly what you need from the ground up.",
      features: ["Custom Development", "API Integration", "Database Design", "Scalable Architecture"],
      gradient: "from-gold-dim/20 to-transparent",
      image: "https://images.unsplash.com/photo-1504639725590-34d0984388bd?auto=format&fit=crop&w=800&q=80"
    }
  ];

  return (
    <div data-testid="services-page" className="relative" ref={containerRef}>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center pt-32 pb-20 overflow-hidden"
      >
        {/* Layered Background */}
        <div className="absolute inset-0">
          {/* Layer 1: Code/Tech Background */}
          <motion.div
            className="absolute inset-0"
            style={{ y: bgY }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=2000&q=80')`,
                filter: 'brightness(0.25) saturate(0.8)'
              }}
            />
          </motion.div>

          {/* Layer 2: Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />

          {/* Layer 3: Mesh */}
          <div className="absolute inset-0 bg-mesh-intense" />
        </div>

        {/* Decorative Glows */}
        <motion.div
          className="absolute top-1/3 left-0 w-80 h-80 bg-violet-glow/15 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.3, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-64 h-64 bg-gold/8 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <motion.span
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block"
          >
            Our Services
          </motion.span>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-none mb-6"
          >
            <span className="text-white">Solutions That</span>
            <br />
            <span className="text-gradient-gold">Transform</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed"
          >
            From concept to execution, we deliver comprehensive digital solutions
            tailored to your unique needs. Every service is crafted with precision
            and built for lasting impact.
          </motion.p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section
        ref={servicesRef}
        className="relative py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className="group relative glass-card rounded-3xl overflow-hidden cursor-pointer"
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                {/* Background Image */}
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url('${service.image}')` }}
                  />
                </div>

                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Content */}
                <div className="relative z-10 p-8 md:p-10">
                  {/* Icon */}
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-all duration-300"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <service.icon size={28} strokeWidth={1.5} className="text-gold" />
                  </motion.div>

                  {/* Title */}
                  <h3 className="font-heading text-2xl md:text-3xl font-medium text-white mb-4 group-hover:text-gold transition-colors">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-white/60 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {service.features.map((feature, idx) => (
                      <motion.span
                        key={idx}
                        className="px-3 py-1 rounded-full bg-white/5 text-white/50 text-xs font-mono group-hover:bg-gold/10 group-hover:text-gold/70 transition-colors"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={servicesInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.5 + idx * 0.1 }}
                      >
                        {feature}
                      </motion.span>
                    ))}
                  </div>

                  {/* Link */}
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-mono text-sm uppercase tracking-widest group/link"
                  >
                    Learn More
                    <ArrowRight size={16} strokeWidth={1.5} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80')`
            }}
          />
          <div className="absolute inset-0 bg-background-paper" />
          <div className="absolute inset-0 bg-mesh opacity-50" />
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={ctaInView ? "visible" : "hidden"}
          className="relative max-w-7xl mx-auto px-6 md:px-12 text-center"
        >
          <motion.h2
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="font-heading text-3xl md:text-5xl font-medium text-white mb-6"
          >
            Don't See What You <span className="text-gradient-gold">Need?</span>
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-10"
          >
            We specialize in building custom solutions. If you have a unique
            challenge, we'd love to hear about it and craft the perfect solution.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                data-testid="services-cta-consultation"
                className="glass-button px-10 py-5 rounded-full font-mono text-sm uppercase tracking-widest text-gold flex items-center gap-3 group animate-pulse-glow"
              >
                Discuss Your Project
                <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.a
              href="tel:+27699512915"
              data-testid="services-cta-call"
              className="px-10 py-5 rounded-full font-mono text-sm uppercase tracking-widest text-white/70 border border-white/10 hover:border-gold/50 hover:text-gold transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Call: +27 699 512 915
            </motion.a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default Services;
